import React from "react";
import { formatDataRateSmart } from "kontrolEdge/helpers/formatDataRate";
import { SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import { MeterBar, OverviewPanel } from "./OverviewPanel";
import { AllocationKey, AllocationSummary } from "./overviewModel";

/** "11%", or "<1%" for a reservation too small to round to one. */
function percentLabel(value: number, total: number): string {
  if (total <= 0) return "0%";
  const percent = (value / total) * 100;
  return percent > 0 && percent < 1 ? "<1%" : `${Math.round(percent)}%`;
}

/**
 * How much of the ports' capacity each service type has reserved.
 *
 * Allocation, not live traffic: the usage payload carries reservations per
 * type and traffic only per port. The draft's "real time vs 5-minute average"
 * per type is out of scope until a source exists (intent, §2).
 */
export function AllocationPanel({
  summary,
  highlight,
  isLoading,
}: {
  summary: AllocationSummary;
  /** The allocation bucket of the selected scope; the other rows dim. */
  highlight: AllocationKey | null;
  isLoading: boolean;
}) {
  const { capacityMbps, allocatedMbps, rows } = summary;
  const freeMbps = Math.max(capacityMbps - allocatedMbps, 0);

  return (
    <OverviewPanel
      title="Capacity allocation"
      subtitle={
        capacityMbps > 0
          ? `Bandwidth each service type reserves out of ${formatDataRateSmart(capacityMbps)} of port capacity`
          : "Bandwidth each service type reserves out of the ports' capacity"
      }
    >
      <AllocationBody
        summary={summary}
        freeMbps={freeMbps}
        highlight={highlight}
        isLoading={isLoading}
        hasRows={rows.length > 0 && capacityMbps > 0}
      />
    </OverviewPanel>
  );
}

function AllocationBody({
  summary,
  freeMbps,
  highlight,
  isLoading,
  hasRows,
}: {
  summary: AllocationSummary;
  freeMbps: number;
  highlight: AllocationKey | null;
  isLoading: boolean;
  hasRows: boolean;
}) {
  const { capacityMbps, allocatedMbps, rows } = summary;

  if (isLoading) {
    return (
      <div className="space-y-4 py-2" aria-busy="true">
        <SkeletonLine h={28} decorative />
        <SkeletonLine h={12} decorative />
        <SkeletonLine h={12} decorative />
      </div>
    );
  }
  if (!hasRows) {
    return (
      <p className="py-8 text-center text-sm text-semantic-text-secondary">
        No bandwidth allocated yet.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <p className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-heading text-2xl font-bold text-semantic-text-primary">
            {percentLabel(allocatedMbps, capacityMbps)}
          </span>
          <span className="text-sm text-semantic-text-secondary">
            {formatDataRateSmart(allocatedMbps)} of {formatDataRateSmart(capacityMbps)} allocated ·{" "}
            {formatDataRateSmart(freeMbps)} free
          </span>
        </p>
        <div className="mt-3">
          <MeterBar value={allocatedMbps} max={capacityMbps} heightClassName="h-2.5" />
        </div>
      </div>

      <ul className="space-y-3">
        {rows.map((row) => {
          const isDimmed = highlight !== null && highlight !== row.key;
          return (
            <li
              key={row.key}
              // The value column has a fixed width so every bar starts and ends at the same x.
              className={`grid grid-cols-[minmax(0,10rem)_1fr_8.5rem] items-center gap-4 transition-opacity ${
                isDimmed ? "opacity-40" : ""
              }`}
            >
              <span className="truncate text-sm text-semantic-text-primary">{row.label}</span>
              <MeterBar value={row.allocatedMbps} max={capacityMbps} />
              <span className="text-right leading-tight">
                <span className="block text-sm font-semibold tabular-nums text-semantic-text-primary">
                  {formatDataRateSmart(row.allocatedMbps)}
                </span>
                <span className="block text-[11px] text-semantic-text-secondary">
                  {percentLabel(row.allocatedMbps, capacityMbps)} of {formatDataRateSmart(capacityMbps)}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
